export type ImagePreloadPriority = 'P0' | 'P1' | 'P2'

export interface ImagePreloadGroups {
  P0?: readonly string[]
  P1?: readonly string[]
  P2?: readonly string[]
}

export interface NetworkInformationLike {
  saveData?: boolean
  effectiveType?: string
  downlink?: number
}

export interface ImagePreloadPolicy {
  maxPriority: 0 | 1 | 2
  concurrency: 1 | 2
  networkClass: 'save-data' | '2g' | '3g' | 'fast' | 'unknown-fast'
}

type PreloadImage = Pick<HTMLImageElement, 'src' | 'decoding' | 'onload' | 'onerror'>

interface QueueItem {
  src: string
  priority: 0 | 1 | 2
  attempts: number
  generation: number
}

export interface ImagePreloadEnvironment {
  createImage: () => PreloadImage
  connection: () => NetworkInformationLike | undefined
  isHidden: () => boolean
  onVisibilityChange: (listener: () => void) => () => void
  setTimeout: (listener: () => void, delay: number) => number
  clearTimeout: (timer: number) => void
  requestIdle: (listener: () => void) => void
}

export interface ImagePreloadSnapshot {
  scopeKey: string
  generation: number
  total: number
  loaded: number
  failed: number
  active: number
  availablePriority: 0 | 1 | 2
  policy: ImagePreloadPolicy
}

const PRIORITIES = ['P0', 'P1', 'P2'] as const

export function resolveImagePreloadPolicy(connection?: NetworkInformationLike): ImagePreloadPolicy {
  if (!connection) return { maxPriority: 2, concurrency: 2, networkClass: 'unknown-fast' }
  const effectiveType = String(connection.effectiveType || '').toLowerCase()
  const downlink = Number(connection.downlink)
  if (connection.saveData || effectiveType === 'slow-2g' || effectiveType === '2g') {
    return { maxPriority: 0, concurrency: 1, networkClass: connection.saveData ? 'save-data' : '2g' }
  }
  if (effectiveType === '3g' || (Number.isFinite(downlink) && downlink > 0 && downlink <= 1.5)) {
    return { maxPriority: 1, concurrency: 1, networkClass: '3g' }
  }
  return { maxPriority: 2, concurrency: 2, networkClass: 'fast' }
}

export class ImagePreloadController {
  private readonly queues: QueueItem[][] = [[], [], []]
  private readonly queued = new Set<string>()
  private readonly activeImages = new Set<PreloadImage>()
  private readonly activePriorities = [0, 0, 0]
  private readonly stopVisibilityListener: () => void
  private generation = 0
  private scopeKey = ''
  private total = 0
  private loaded = 0
  private failed = 0
  private active = 0
  private availablePriority: 0 | 1 | 2 = 0
  private higherPrioritiesReleased = false
  private releaseTimer = 0
  private policy: ImagePreloadPolicy

  constructor(private readonly environment: ImagePreloadEnvironment = browserImagePreloadEnvironment()) {
    this.policy = resolveImagePreloadPolicy(environment.connection())
    this.stopVisibilityListener = environment.onVisibilityChange(() => {
      if (!environment.isHidden()) this.pump()
    })
  }

  replaceScope(scopeKey: string, requestedGroups: ImagePreloadGroups): void {
    const normalizedKey = String(scopeKey || 'runtime')
    if (normalizedKey === this.scopeKey) return
    this.reset(normalizedKey)
    this.policy = resolveImagePreloadPolicy(this.environment.connection())
    const groups = normalizeGroups(requestedGroups)
    PRIORITIES.forEach((key, priority) => {
      if (priority > this.policy.maxPriority) return
      for (const src of groups[key]) {
        if (this.queued.has(src)) continue
        this.queued.add(src)
        this.queues[priority]?.push({ src, priority: priority as 0 | 1 | 2, attempts: 0, generation: this.generation })
      }
    })
    this.total = this.queued.size
    this.pump()
    if (this.total && this.policy.maxPriority > 0) {
      this.releaseTimer = this.environment.setTimeout(() => {
        this.environment.requestIdle(() => {
          this.higherPrioritiesReleased = true
          this.advancePriority()
          this.pump()
        })
      }, 800)
    }
  }

  snapshot(): ImagePreloadSnapshot {
    return {
      scopeKey: this.scopeKey, generation: this.generation, total: this.total, loaded: this.loaded,
      failed: this.failed, active: this.active, availablePriority: this.availablePriority, policy: this.policy,
    }
  }

  dispose(): void {
    this.reset('disposed')
    this.stopVisibilityListener()
  }

  private reset(scopeKey: string): void {
    this.generation += 1
    this.scopeKey = scopeKey
    this.environment.clearTimeout(this.releaseTimer)
    this.releaseTimer = 0
    for (const queue of this.queues) queue.splice(0)
    this.queued.clear()
    for (const image of this.activeImages) {
      image.onload = null
      image.onerror = null
      try { image.src = '' } catch { /* Browser cancellation is best effort. */ }
    }
    this.activeImages.clear()
    this.activePriorities.fill(0)
    this.total = 0
    this.loaded = 0
    this.failed = 0
    this.active = 0
    this.availablePriority = 0
    this.higherPrioritiesReleased = false
  }

  private advancePriority(): void {
    if (!this.higherPrioritiesReleased) return
    while (this.availablePriority < this.policy.maxPriority) {
      const priority = this.availablePriority
      if ((this.queues[priority]?.length || 0) > 0 || (this.activePriorities[priority] || 0) > 0) return
      this.availablePriority = (priority + 1) as 1 | 2
    }
  }

  private next(): QueueItem | undefined {
    const maxPriority = Math.min(this.availablePriority, this.policy.maxPriority)
    for (let priority = 0; priority <= maxPriority; priority += 1) {
      const item = this.queues[priority]?.shift()
      if (item) return item
    }
    return undefined
  }

  private pump(): void {
    if (this.environment.isHidden()) return
    this.advancePriority()
    const concurrency = this.availablePriority === 0 ? this.policy.concurrency : 1
    while (this.active < concurrency) {
      const item = this.next()
      if (!item) return
      if (item.generation !== this.generation) continue
      const image = this.environment.createImage()
      image.decoding = 'async'
      this.active += 1
      this.activePriorities[item.priority] = (this.activePriorities[item.priority] || 0) + 1
      this.activeImages.add(image)
      const settle = (success: boolean) => {
        image.onload = null
        image.onerror = null
        if (item.generation !== this.generation) return
        this.activeImages.delete(image)
        this.active -= 1
        this.activePriorities[item.priority] = Math.max(0, (this.activePriorities[item.priority] || 0) - 1)
        if (success) this.loaded += 1
        else if (item.attempts < 1) this.queues[item.priority]?.push({ ...item, attempts: item.attempts + 1 })
        else this.failed += 1
        this.advancePriority()
        this.pump()
      }
      image.onload = () => settle(true)
      image.onerror = () => settle(false)
      image.src = item.src
    }
  }
}

function normalizeGroups(groups: ImagePreloadGroups): Record<ImagePreloadPriority, string[]> {
  const result: Record<ImagePreloadPriority, string[]> = { P0: [], P1: [], P2: [] }
  const seen = new Set<string>()
  for (const key of PRIORITIES) {
    for (const value of groups[key] || []) {
      const src = String(value || '').trim()
      if (!src || src.startsWith('data:') || src.startsWith('blob:') || seen.has(src)) continue
      seen.add(src)
      result[key].push(src)
    }
  }
  return result
}

function browserImagePreloadEnvironment(): ImagePreloadEnvironment {
  const browserNavigator = navigator as Navigator & { connection?: NetworkInformationLike; mozConnection?: NetworkInformationLike; webkitConnection?: NetworkInformationLike }
  return {
    createImage: () => new Image(),
    connection: () => browserNavigator.connection || browserNavigator.mozConnection || browserNavigator.webkitConnection,
    isHidden: () => document.visibilityState === 'hidden',
    onVisibilityChange: (listener) => {
      document.addEventListener('visibilitychange', listener)
      return () => document.removeEventListener('visibilitychange', listener)
    },
    setTimeout: (listener, delay) => window.setTimeout(listener, delay),
    clearTimeout: (timer) => window.clearTimeout(timer),
    requestIdle: (listener) => {
      const idleWindow = window as Window & { requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number }
      if (idleWindow.requestIdleCallback) idleWindow.requestIdleCallback(listener, { timeout: 1200 })
      else listener()
    },
  }
}
